import ReactMarkdown from "react-markdown";
import { HubUpdatesLink } from "./HubUpdatesLink";
import { IconSection } from "./IconSection";
import { finishPlace } from "@/components/shared/StepperModal";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useHubUpdatesStore } from "@/store/hubUpdatesStore";
import { highlightExchangeInTitle, renderHighlighted } from "@/utils/hubUpdates";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Image from "next/image";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

const markdownRemarkPlugins = [remarkGfm];
const markdownRehypePlugins = [rehypeRaw];

interface HubUpdatesContentProps {
  title: string;
  description?: string;
  imageUrl?: string;
  link?: string;
  publishedAt?: string;
  onClose: (place: (typeof finishPlace)[keyof typeof finishPlace]) => void;
  isActive: boolean;
  index: number;
  currentIndex: number;
  totalSteps: number;
  onNext: () => void;
  onBack: () => void;
}

export const HubUpdatesContent = ({
  title,
  description,
  imageUrl,
  link,
  publishedAt,
  onClose,
  isActive,
  index,
  currentIndex,
  totalSteps,
  onNext,
  onBack,
}: HubUpdatesContentProps) => {
  const { markLatestUpdateAsSeen } = useHubUpdatesStore();
  const isFirstStep = index === 0;
  const isLastStep = index === totalSteps - 1;

  const handleLinkClick = () => {
    // Mark this update as seen when user clicks the link
    if (publishedAt) {
      markLatestUpdateAsSeen(publishedAt);
    }
    // Open the link in a new tab
    if (link) {
      window.open(link, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      className={cn(
        "absolute inset-0 flex h-full min-h-0 transition-opacity duration-300 ease-in-out",
        isActive ? "opacity-100" : "opacity-0",
        isActive ? "pointer-events-auto" : "pointer-events-none",
        // Mobile: column layout
        "flex-col",
        // Desktop: row layout
        "md:flex-row",
      )}
    >
      {/* Left Part: Title, Description, CTA, Navigation */}
      <div
        className={cn(
          "relative grid grid-rows-[auto,1fr,auto] gap-4 md:gap-5 order-2 md:order-1 h-full min-h-0",
          "md:w-[55%] flex-1 p-6 md:py-8 md:px-10",
          "rounded-b-large md:rounded-none md:rounded-l-large overflow-hidden",
        )}
        style={{
          background: "linear-gradient(90deg, var(--color-cede-bg-modal) 0%, rgba(21,26,34,0.92) 70%, #0B0D15 100%)",
        }}
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-24 top-6 h-56 w-56 rounded-full bg-[#1c2f48] blur-[120px] opacity-70" />
          <div className="absolute left-1/3 -bottom-24 h-72 w-72 rounded-full bg-[#0f1a2e] blur-[140px] opacity-80" />
          <div className="absolute -right-28 top-1/3 h-56 w-56 rounded-full bg-cede-primary/20 blur-[130px]" />
        </div>

        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-cede-text-secondary">Hub updates</p>

        <div className="min-h-0 flex flex-col gap-4 md:gap-6">
          <h2 className="text-2xl md:text-4xl font-bold leading-tight text-cede-text-primary text-center md:text-left">
            {highlightExchangeInTitle(title)}
          </h2>

          <div
            className={cn(
              "flex-1 min-h-0 overflow-y-auto scrollbar-visible text-left text-cede-text-primary [&_p]:text-base [&_p]:leading-relaxed [&_p]:m-0 [&_p:not(:first-of-type)]:mt-1 pr-2",
            )}
          >
            <ReactMarkdown
              components={{
                p: ({ children }) => <p>{renderHighlighted(children)}</p>,
                strong: ({ children }) => <strong className="font-bold">{renderHighlighted(children)}</strong>,
                em: ({ children }) => <em className="italic">{renderHighlighted(children)}</em>,
                li: ({ children }) => <li className="list-disc ml-4">{renderHighlighted(children)}</li>,
                a: ({ children, href }) => <HubUpdatesLink href={href}>{children}</HubUpdatesLink>,
                section: ({ children, ...props }) => {
                  const icon = (props as { icon?: string })?.icon;
                  const variant = ((props as { variant?: string })?.variant || "default") as
                    | "default"
                    | "highlight"
                    | "warning"
                    | "success";
                  return (
                    <IconSection icon={icon} variant={variant}>
                      {children}
                    </IconSection>
                  );
                },
              }}
              remarkPlugins={markdownRemarkPlugins}
              rehypePlugins={markdownRehypePlugins}
            >
              {description}
            </ReactMarkdown>
          </div>

          {link && (
            <div className="flex justify-center pt-2">
              <Button onClick={handleLinkClick} size="medium" className="px-8">
                Learn More
              </Button>
            </div>
          )}
        </div>

        {/* Navigation: Back/Close | Dots | Next */}
        <div className="flex items-center justify-between gap-4 pt-1 mt-auto">
          {/* Back or Close Button */}
          <Button
            variant={"outline"}
            size="medium"
            onClick={onBack}
            disabled={isFirstStep && !isLastStep}
            className="min-w-24"
          >
            <>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </>
          </Button>

          {/* Step Dots - Centered */}
          <div className="flex justify-center items-center gap-2 flex-1">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  "w-2 h-2 rounded-full transition-colors duration-200",
                  i === currentIndex ? "bg-cede-primary" : "bg-cede-text-secondary/30",
                )}
              />
            ))}
          </div>

          {/* Next Button */}
          <Button
            variant={isLastStep ? "primary" : "outline"}
            size="medium"
            onClick={isLastStep ? () => onClose(finishPlace.FINISH_BUTTON) : onNext}
            className="min-w-24"
          >
            {isLastStep ? (
              "Close"
            ) : (
              <>
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Right Part: Image with dark background */}
      <div
        className={cn(
          "order-1 md:order-2 flex items-center justify-center relative",
          "md:w-[45%]",
          // On mobile, keep image section shorter so description is visible
          "h-[32vh] max-h-[300px] min-h-[180px] md:h-auto md:min-h-full",
          "rounded-t-large md:rounded-none md:rounded-r-large overflow-hidden",
          "bg-[#0B0D15]",
        )}
      >
        {imageUrl && (
          <div className="relative w-full h-full p-4 md:p-6">
            <Image src={imageUrl} alt={title} fill sizes="(max-width: 768px) 100vw, 50vw" className="object-contain" />
          </div>
        )}
      </div>
    </div>
  );
};
